import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import ResponsiveImage from '@/components/ResponsiveImage';
import HtmlDescription from '@/components/HtmlDescription';

import { productService, ProductError } from './productService';
import { DLC, Game, GamePurchaseOffer, Product } from './types';
import { addToCart } from '../cart/cartSlice';
import { cartService } from '../cart/cartService';
import { favoritesService } from '../favorites/favoritesService';
import { originalPrice } from './discount';

const formatPrice = (price: number) => `${price.toLocaleString('ru-RU')} ₽`;
const activeDiscount = (offer: GamePurchaseOffer) => Boolean(
  offer.discountPercent &&
  (!offer.discountEndsAt || new Date(offer.discountEndsAt).getTime() > Date.now()),
);
const formatDiscountEnd = (value?: string | null) => value
  ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(value))
  : null;
const formatDate = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' }).format(d);
};

const Badge: React.FC<{ children: React.ReactNode; accent?: boolean }> = ({ children, accent }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
    accent
      ? 'border border-red-500/40 bg-red-500/15 text-red-600 dark:text-red-300'
      : 'border border-foreground/10 bg-foreground/6 text-foreground/70'
  }`}>
    {children}
  </span>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="rounded-[28px] bg-card p-6 sm:p-8">
    <h2 className="mb-5 text-xs font-semibold uppercase tracking-[0.22em] text-foreground/40">{title}</h2>
    {children}
  </section>
);

const offerMeta = (offer: GamePurchaseOffer) => {
  if (offer.fulfillmentType === 'SharedAccount') return {
    icon: 'sharing',
    title: offer.activationType === 'Included' ? 'С активацией' : 'Без активации',
    subtitle: 'Шеринг аккаунта',
  };
  return {
    icon: offer.region === 'India' ? '🇮🇳' : offer.region === 'Turkey' ? '🇹🇷' : '🌐',
    title: ({ India: 'Индия', Turkey: 'Турция', CIS: 'СНГ' }[offer.region!] ?? offer.region ?? 'Регион'),
    subtitle: 'Покупка в регионе',
  };
};

const SharingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-6 w-6" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ProductPageSkeleton = () => (
  <div className="min-h-screen bg-background px-4 py-8">
    <div className="mx-auto max-w-[90rem] animate-pulse space-y-6">
      <div className="h-4 w-48 rounded bg-foreground/5" />
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <div className="aspect-square sm:aspect-[5/6] rounded-[32px] bg-foreground/5" />
        <div className="space-y-4">
          <div className="h-10 w-3/4 rounded bg-foreground/5" />
          <div className="h-6 w-32 rounded bg-foreground/5" />
          <div className="h-14 w-full rounded-2xl bg-foreground/5" />
        </div>
      </div>
    </div>
  </div>
);

const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedOffer, setSelectedOffer] = useState<GamePurchaseOffer | null>(null);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (!id || Number.isNaN(Number(id))) {
      setError('Некорректный ID товара');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    productService.fetchProduct(Number(id))
      .then(r => { setProduct(r.product); setSelectedId(r.product.id); })
      .catch(err => setError((err as ProductError).message || 'Не удалось загрузить товар'))
      .finally(() => setLoading(false));
    favoritesService.getStatus(Number(id))
      .then(r => setIsFavorite(r.isFavorite))
      .catch(() => {});
  }, [id]);

  const handleToggleFavorite = async () => {
    if (!product) return;
    try {
      if (isFavorite) {
        await favoritesService.remove(product.id);
        setIsFavorite(false);
      } else {
        await favoritesService.add(product.id);
        setIsFavorite(true);
      }
    } catch {}
  };

  const handleAddToCart = async () => {
    if (!product) return;
    const pid = selectedOffer?.id ?? selectedId ?? product.id;
    try {
      await cartService.add(pid);
      dispatch(addToCart(pid));
      setIsAddedToCart(true);
    } catch {}
  };

  if (loading) return <ProductPageSkeleton />;

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background px-4 py-8 text-foreground">
        <div className="mx-auto max-w-[90rem]">
          <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-foreground">
            ← Главная
          </Link>
          <div className="rounded-[32px] border border-foreground/10 bg-foreground/5 py-16 text-center">
            <p className="mb-2 text-lg font-medium">{error?.includes('not found') ? 'Товар не найден' : 'Ошибка загрузки'}</p>
            <p className="mb-6 text-gray-400">{error}</p>
            <Link to="/" className="rounded-full bg-red-600 px-6 py-3 text-sm font-semibold hover:bg-red-500">На главную</Link>
          </div>
        </div>
      </div>
    );
  }

  const game = product as Game;
  const dlcList: DLC[] = Array.isArray((product as any).listDLC) ? (product as any).listDLC : [];
  const selectedDLC = selectedId !== product.id ? dlcList.find(d => d.id === selectedId) : null;
  const active = selectedDLC ?? product;
  const purchaseOffers = game.store === 'PlayStationStore'
    ? ((selectedDLC?.purchaseOffers ?? game.purchaseOffers) ?? []).filter(o => o.isAvailable) : [];
  const regionOffers = purchaseOffers.filter(o => o.fulfillmentType === 'PersonalPurchase');
  const sharingOffers = purchaseOffers.filter(o => o.fulfillmentType === 'SharedAccount');
  const screenshots: string[] = Array.isArray(game.screenshots) ? game.screenshots : [];

  return (
    <div className="min-h-screen overflow-x-hidden bg-background py-6 text-foreground sm:py-10">
      <div className="mx-auto max-w-[90rem] px-4 sm:px-6">

        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-foreground transition-colors">Главная</Link>
          <span>·</span>
          <Link to="/catalog" className="hover:text-foreground transition-colors">Каталог</Link>
          <span>·</span>
          <span className="truncate text-gray-300">{product.name}</span>
        </nav>

        {/* Hero: cover + buy panel */}
        <div className="grid gap-6 lg:grid-cols-[380px_1fr] xl:grid-cols-[420px_1fr] xl:gap-10">

          {/* Cover */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="overflow-hidden rounded-[32px] border border-foreground/10 shadow-[0_32px_80px_rgba(0,0,0,0.1)]">
              <ResponsiveImage
                src={active.picture}
                sources={active.pictureSources}
                sizes="(min-width: 1280px) 420px, (min-width: 1024px) 380px, 100vw"
                alt={active.name}
                className="aspect-square sm:aspect-[5/6] w-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>

          {/* Info + buy */}
          <div className="flex flex-col gap-5">

            {/* Title + badges */}
            <div>
              <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl xl:text-[2.75rem]">
                {active.name}
              </h1>

              {/* Platform / genre badges */}
              {(game.platforms?.length > 0 || game.genres?.length > 0) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {game.platforms?.map(p => <Badge key={p} accent>{p}</Badge>)}
                  {game.genres?.map(g => <Badge key={g}>{g}</Badge>)}
                </div>
              )}
            </div>

            {/* Price + buy */}
            <div className="rounded-[28px] border border-foreground/10 bg-card p-6">
              {purchaseOffers.length > 0 && (
                <div className="mb-6 space-y-5">
                  {regionOffers.length > 0 && <div>
                    <div className="mb-3 flex items-center justify-between gap-3"><div><p className="text-sm font-semibold text-foreground">Выберите регион</p><p className="mt-0.5 text-xs text-foreground/45">Игра приобретается в выбранном регионе</p></div><span className="rounded-full bg-foreground/5 px-3 py-1 text-[11px] font-medium text-foreground/45">PS Store</span></div>
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {regionOffers.map(offer => {
                        const meta = offerMeta(offer); const selected = selectedOffer?.id === offer.id;
                        return <button key={offer.id} type="button" onClick={() => { setSelectedOffer(offer); setIsAddedToCart(false); }} className={`group relative flex items-center gap-3 rounded-2xl border p-3.5 text-left transition-all ${selected ? 'border-red-500 bg-red-500/10 shadow-[0_0_0_1px_rgba(239,68,68,.15)]' : 'border-foreground/10 bg-foreground/[.035] hover:border-foreground/25 hover:bg-foreground/[.06]'}`}>
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-foreground/5 text-2xl shadow-sm">{meta.icon}</span>
                          <span className="min-w-0 flex-1"><span className="flex items-center gap-2 text-sm font-semibold text-foreground">{meta.title}{activeDiscount(offer) && <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] text-white">−{offer.discountPercent}%</span>}</span><span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-foreground/45"><span>{formatPrice(offer.price)}</span>{originalPrice(offer) && <span className="line-through decoration-red-500/70">{formatPrice(originalPrice(offer)!)}</span>}{activeDiscount(offer) && formatDiscountEnd(offer.discountEndsAt) ? <span>до {formatDiscountEnd(offer.discountEndsAt)}</span> : null}</span></span>
                          <span className={`flex h-5 w-5 items-center justify-center rounded-full border text-xs ${selected ? 'border-red-500 bg-red-500 text-white' : 'border-foreground/15 text-transparent'}`}>✓</span>
                        </button>;
                      })}
                    </div>
                  </div>}
                  {sharingOffers.length > 0 && <div className="rounded-2xl border border-foreground/10 bg-foreground/[.025] p-4">
                    <div className="mb-3 flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/15 text-violet-500 dark:text-violet-300"><SharingIcon /></span><div><p className="text-sm font-semibold text-foreground">Шеринг аккаунта</p><p className="text-xs text-foreground/45">Получите данные аккаунта с игрой — дешевле обычной покупки</p></div></div>
                    <div className="grid gap-2 sm:grid-cols-2">{sharingOffers.map(offer => { const meta = offerMeta(offer); const selected = selectedOffer?.id === offer.id; return <button key={offer.id} type="button" onClick={() => { setSelectedOffer(offer); setIsAddedToCart(false); }} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all ${selected ? 'border-violet-500 bg-violet-500/10' : 'border-foreground/10 bg-card hover:border-foreground/25'}`}><span><span className="block text-sm font-medium">{meta.title}</span><span className="text-xs text-foreground/45">{meta.subtitle}</span></span><span className="font-semibold text-foreground">{formatPrice(offer.price)}</span></button>; })}</div>
                  </div>}
                  {selectedOffer?.fulfillmentType === 'SharedAccount' && <p className="rounded-xl border border-violet-500/15 bg-violet-500/[.07] px-4 py-3 text-xs leading-relaxed text-foreground/60">Вы получите данные аккаунта, на котором уже приобретена игра. Инструкция по активации будет предоставлена вместе с заказом.</p>}
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <Button
                  type="button"
                  onClick={handleToggleFavorite}
                  aria-label={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
                  variant="ghost"
                  className={`flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-[18px] border transition-colors ${
                    isFavorite
                      ? 'border-red-500/60 bg-red-500/15 text-red-600 dark:text-red-400 hover:bg-red-500/25'
                      : 'border-foreground/10 bg-foreground/5 text-foreground/50 hover:border-foreground/20 hover:text-foreground'
                  }`}
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                      d="m12 20.5-1.45-1.32C5.4 14.5 2 11.36 2 7.5A4.5 4.5 0 0 1 6.5 3 5 5 0 0 1 12 6.1 5 5 0 0 1 17.5 3 4.5 4.5 0 0 1 22 7.5c0 3.86-3.4 7-8.55 11.68L12 20.5Z" />
                  </svg>
                </Button>

                <Button
                  onClick={isAddedToCart ? () => navigate('/cart') : handleAddToCart}
                  disabled={purchaseOffers.length > 0 && !selectedOffer}
                  variant="ghost"
                  className={`flex-1 h-[58px] rounded-[18px] py-4 text-base font-semibold transition-all ${
                    isAddedToCart
                      ? 'bg-green-600 hover:bg-green-500'
                      : 'bg-[linear-gradient(90deg,#991b1b,#dc2626)] hover:opacity-90'
                  }`}
                >
                  {isAddedToCart ? 'Перейти в корзину →' : purchaseOffers.length > 0 && !selectedOffer ? 'Выберите вариант' : 'Добавить в корзину'}
                </Button>
              </div>
            </div>

            {/* Details grid */}
            {(formatDate(game.dateRelease) || game.completionTime || game.online || game.multiplayer) && (
              <Section title="Детали">
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
                  {formatDate(game.dateRelease) && (
                    <div>
                      <p className="text-sm text-foreground/40">Дата выхода</p>
                      <p className="mt-1.5 text-base font-medium text-foreground">{formatDate(game.dateRelease)}</p>
                    </div>
                  )}
                  {!!game.completionTime && (
                    <div>
                      <p className="text-sm text-foreground/40">Прохождение</p>
                      <p className="mt-1.5 text-base font-medium text-foreground">~{game.completionTime} ч.</p>
                    </div>
                  )}
                  {game.online && (
                    <div>
                      <p className="text-sm text-foreground/40">Онлайн</p>
                      <p className="mt-1.5 text-base font-medium text-green-400">Есть</p>
                    </div>
                  )}
                  {game.multiplayer && (
                    <div>
                      <p className="text-sm text-foreground/40">Вдвоём на одном экране</p>
                      <p className="mt-1.5 text-base font-medium text-green-400">Есть</p>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* DLC / Editions */}
            {dlcList.length > 0 && (
              <Section title="Издания и дополнения">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button
                    type="button"
                    onClick={() => { setSelectedId(product.id); setSelectedOffer(null); setIsAddedToCart(false); }}
                    variant="ghost"
                    className={`rounded-[20px] border p-4 text-left transition-all h-auto w-full justify-start ${
                      selectedId === product.id
                        ? 'border-red-500/60 bg-red-500/10'
                        : 'border-foreground/10 bg-foreground/4 hover:border-foreground/20'
                    }`}
                  >
                    <p className="text-sm font-medium text-foreground">{product.name}</p>
                  </Button>

                  {dlcList.map(dlc => (
                    <Button
                      key={dlc.id}
                      type="button"
                      onClick={() => { setSelectedId(dlc.id); setSelectedOffer(null); setIsAddedToCart(false); }}
                      variant="ghost"
                      className={`rounded-[20px] border p-4 text-left transition-all h-auto w-full justify-start ${
                        selectedId === dlc.id
                          ? 'border-red-500/60 bg-red-500/10'
                          : 'border-foreground/10 bg-foreground/4 hover:border-foreground/20'
                    }`}
                  >
                      <p className="text-sm font-medium text-foreground">{dlc.name}</p>
                    </Button>
                  ))}
                </div>
              </Section>
            )}
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="mt-6">
            <Section title="Описание">
              <HtmlDescription
                html={product.description}
                className="text-base leading-6 text-foreground/80"
              />
            </Section>
          </div>
        )}

        {/* Screenshots */}
        {screenshots.length > 0 && (
          <div className="mt-6">
            <Section title="Скриншоты">
              <Carousel opts={{ align: 'start', loop: screenshots.length > 1 }}>
                <CarouselContent className="-ml-3">
                  {screenshots.map((src, i) => (
                    <CarouselItem key={i} className="pl-3 md:basis-1/2 xl:basis-1/3">
                      <div className="overflow-hidden rounded-[18px]">
                        <ResponsiveImage
                          src={src}
                          sources={game.screenshotSources?.[i]}
                          sizes="(min-width: 1280px) 33vw, (min-width: 768px) 50vw, 100vw"
                          alt={`Скриншот ${i + 1}`}
                          className="aspect-video w-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </Section>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProductPage;
